'use server'

import { apiRequest, ApiResponse } from '../request'

export type DispensationStatus =
    | 'DISP_PENDING'
    | 'DISP_SEPARATED'
    | 'DISP_CHECKED'
    | 'DISP_DISPENSED'
    | 'DISP_PARTIALLY_DISPENSED'
    | 'DISP_CANCELLED'
    | 'DISP_RETURNED'

export interface Dispensation {
    id: number
    companyId: number
    prescriptionId?: number
    prescriptionItemId?: number
    clientId: number
    medicationId: number
    batchId?: number
    prescribedQuantity: number
    dispensedQuantity: number
    returnedQuantity: number
    isControlled: boolean
    isSubstitution: boolean
    substitutionReason?: string
    originalMedicationId?: number
    instructions?: string
    notes?: string
    alerts?: any[]
    alertsAcknowledged: boolean
    alertsAcknowledgedById?: number
    status: DispensationStatus
    separatedById?: number
    separatedAt?: string
    checkedById?: number
    checkedAt?: string
    dispensedById?: number
    dispensedAt?: string
    stockMovementId?: number
    metadata?: any
    createdAt: string
    updatedAt: string
    medication?: {
        id: number
        name: string
        genericName?: string
        unit: string
        requiresSngpc: boolean
        controlType?: string
    }
    client?: {
        id: number
        name: string
        cpf?: string
    }
    batch?: {
        id: number
        batchNumber: string
        expirationDate?: string
        location?: string
    }
    separatedBy?: {
        id: number
        name: string
    }
    checkedBy?: {
        id: number
        name: string
    }
    dispensedBy?: {
        id: number
        name: string
    }
}

export interface DispensationQueueItem {
    id: number
    companyId: number
    prescriptionId?: number
    clientId: number
    priority: number
    status: 'WAITING' | 'IN_PROGRESS' | 'COMPLETED'
    requestedAt: string
    startedAt?: string
    completedAt?: string
    assignedToId?: number
    client?: {
        id: number
        name: string
    }
    assignedTo?: {
        id: number
        name: string
    }
}

export interface CreateDispensationData {
    prescriptionId?: number
    prescriptionItemId?: number
    clientId: number
    medicationId: number
    batchId?: number
    prescribedQuantity: number
    isControlled?: boolean
    isSubstitution?: boolean
    substitutionReason?: string
    originalMedicationId?: number
    instructions?: string
    notes?: string
    metadata?: any
}

export interface DispenseData {
    dispensedQuantity: number
    batchId?: number
    notes?: string
}

export interface ReturnMedicationData {
    returnedQuantity: number
    reason: string
    notes?: string
}

export interface DispensationFilters {
    clientId?: number
    medicationId?: number
    prescriptionId?: number
    status?: DispensationStatus
    startDate?: string
    endDate?: string
}

// Dispensation CRUD
export async function getDispensations(
    filters?: DispensationFilters
): Promise<ApiResponse<Dispensation[]>> {
    return apiRequest<Dispensation[]>('stock/dispensation', { params: filters })
}

export async function getDispensation(
    id: number
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(`stock/dispensation/${id}`, {})
}

export async function createDispensation(
    data: CreateDispensationData
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>('stock/dispensation', { data }, 'post')
}

// Workflow
export async function separateDispensation(
    id: number
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/separate`,
        {},
        'patch'
    )
}

export async function checkDispensation(
    id: number
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/check`,
        {},
        'patch'
    )
}

export async function dispense(
    id: number,
    data: DispenseData
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/dispense`,
        { data },
        'patch'
    )
}

export async function cancelDispensation(
    id: number,
    reason: string
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/cancel`,
        { data: { reason } },
        'patch'
    )
}

export async function returnMedication(
    id: number,
    data: ReturnMedicationData
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/return`,
        { data },
        'post'
    )
}

export async function acknowledgeAlerts(
    id: number,
    notes?: string
): Promise<ApiResponse<Dispensation>> {
    return apiRequest<Dispensation>(
        `stock/dispensation/${id}/acknowledge-alerts`,
        { data: { notes } },
        'patch'
    )
}

// Queue
export async function getDispensationQueue(): Promise<
    ApiResponse<DispensationQueueItem[]>
> {
    return apiRequest<DispensationQueueItem[]>('stock/dispensation/queue', {})
}

export async function assignQueueItem(
    id: number
): Promise<ApiResponse<DispensationQueueItem>> {
    return apiRequest<DispensationQueueItem>(
        `stock/dispensation/queue/${id}/assign`,
        {},
        'patch'
    )
}

export async function completeQueueItem(
    id: number
): Promise<ApiResponse<DispensationQueueItem>> {
    return apiRequest<DispensationQueueItem>(
        `stock/dispensation/queue/${id}/complete`,
        {},
        'patch'
    )
}

// Labels
export const DISPENSATION_STATUS_LABELS: Record<DispensationStatus, string> = {
    DISP_PENDING: 'Pendente',
    DISP_SEPARATED: 'Separado',
    DISP_CHECKED: 'Conferido',
    DISP_DISPENSED: 'Dispensado',
    DISP_PARTIALLY_DISPENSED: 'Parcialmente Dispensado',
    DISP_CANCELLED: 'Cancelado',
    DISP_RETURNED: 'Devolvido',
}

export const DISPENSATION_STATUS_COLORS: Record<DispensationStatus, string> = {
    DISP_PENDING: 'badge-ghost',
    DISP_SEPARATED: 'badge-info',
    DISP_CHECKED: 'badge-warning',
    DISP_DISPENSED: 'badge-success',
    DISP_PARTIALLY_DISPENSED: 'badge-warning',
    DISP_CANCELLED: 'badge-error',
    DISP_RETURNED: 'badge-accent',
}
